import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Center,
  Divider,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { RiCheckLine, RiErrorWarningLine } from "@remixicon/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Restricted } from "@/app/components/Restricted";
import { EditIconButton } from "@/common/components/CommonIconButtons";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSelect from "@/common/components/Form/FormSelect";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { compactFormattedDate } from "@/common/utils/datetime";
import { LinearConnectionIcon } from "@/integration/components/linear/LinearConnectionIcon";
import { useLinearConnection } from "@/integration/data/useLinearConnection";
import { useLinearDeleteMutation } from "@/integration/data/useLinearDeleteMutation";
import { useLinearPatchMutation } from "@/integration/data/useLinearPatchMutation";
import useLinearInitiateConnectionUrl from "@/integration/hooks/useLinearInitiateConnectionUrl";
import { useFlag } from "@reflag/react-sdk";
import {
  LinearConnection,
  PatchLinearConnectionSchema,
} from "@reflag/shared/linearConnectionAPI";

const description =
  "Linear project updates and comments for flag access and stage changes.";

function Title() {
  return (
    <HStack>
      <LinearConnectionIcon />
      <Heading fontWeight="medium" size="sm">
        Linear
      </Heading>
    </HStack>
  );
}

function ConnectButton() {
  const connectionUrl = useLinearInitiateConnectionUrl();
  return (
    <Restricted role="admin">
      <Button as="a" href={connectionUrl} size="sm" variant="outline">
        Connect
      </Button>
    </Restricted>
  );
}

function Connected({
  linearConnection,
}: {
  linearConnection: LinearConnection;
}) {
  const { org } = useEnvironment();
  const { isEnabled: isAiAgentEnabled } = useFlag("ai-linear-agent");
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: deleteConnection, isPending: mutationIsLoading } =
    useLinearDeleteMutation();

  const form = useForm({
    resolver: zodResolver(PatchLinearConnectionSchema),
    mode: "all",
    defaultValues: {
      appId: linearConnection.appId,
    },
  });

  const updateConnectionMutation = useLinearPatchMutation();

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    updateConnectionMutation,
    (data) => {
      setIsEditing(false);
      form.reset({
        appId: data.appId,
      });
    },
    {
      successToast: "Linear app association saved",
      errorToast: "Failed to update Linear app association",
    },
  );

  const rightActions = (
    <ButtonGroup>
      <DocumentationRefButton section="integrations/linear" />
      <Restricted role="admin">
        <Button
          isLoading={mutationIsLoading}
          size="sm"
          variant="outline"
          onClick={() => deleteConnection()}
        >
          Disconnect
        </Button>
      </Restricted>
    </ButtonGroup>
  );

  return (
    <VStack align="flex-start" spacing={4} w="full">
      <VStack align="flex-start" spacing={2} w="full">
        <HStack justify="space-between" w="full">
          <Title />
          <Badge colorScheme="green">
            <HStack spacing={1}>
              <RiCheckLine size={16} />
              <Text>
                Since {compactFormattedDate(linearConnection.createdAt)}
              </Text>
            </HStack>
          </Badge>
        </HStack>

        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          <Text as="span">{description}</Text>{" "}
          {isAiAgentEnabled && (
            <Text as="span">
              You can use <strong>@reflag</strong> mentions to interact with
              your flags directly within Linear.
            </Text>
          )}
        </Text>
      </VStack>

      <Divider />

      {isAiAgentEnabled ? (
        isEditing ? (
          <FormProvider {...form}>
            <VStack
              align="flex-start"
              as="form"
              spacing={2}
              w="full"
              onSubmit={handleSubmit}
            >
              <VStack spacing={2} w="sm">
                <FormSelect
                  bg="appBackground"
                  description={
                    <Text>
                      App used by the <strong>@reflag</strong> agent in Linear
                      for all operations.
                    </Text>
                  }
                  isDisabled={updateConnectionMutation.isPending}
                  label="App"
                  name="appId"
                  options={org.apps.map((app) => ({
                    label: app.name,
                    value: app.id,
                  }))}
                />

                <FormRootError />
              </VStack>

              <HStack justify="space-between" w="full">
                <ButtonGroup>
                  <FormSubmit
                    isLoading={updateConnectionMutation.isPending}
                    showPostSubmit
                  />
                  <Button
                    color="dimmed"
                    variant="ghost"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
                {rightActions}
              </HStack>
            </VStack>
          </FormProvider>
        ) : (
          <HStack justify="space-between" w="full">
            <HStack fontSize="sm" spacing={1}>
              <Text color="dimmed">App:</Text>
              <Text fontWeight="medium">
                {org.apps.find((app) => app.id === linearConnection.appId)
                  ?.name || "Not selected"}
              </Text>
              <EditIconButton
                label="Edit app association"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </EditIconButton>
            </HStack>
            {rightActions}
          </HStack>
        )
      ) : (
        <Flex justify="flex-end" w="full">
          {rightActions}
        </Flex>
      )}
    </VStack>
  );
}

function NeedsReauthorization() {
  return (
    <VStack align="flex-start" spacing={2} w="full">
      <HStack justify="space-between" w="full">
        <Title />
        <Badge colorScheme="orange">
          <HStack spacing={1}>
            <RiErrorWarningLine size={16} />
            <Text fontSize="sm">Requires reauthorization</Text>
          </HStack>
        </Badge>
      </HStack>

      <HStack justify="space-between" w="full">
        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          Connection to Linear has expired or is incomplete. Reauthorize to
          continue.
        </Text>

        <ButtonGroup>
          <DocumentationRefButton section="integrations/linear" />
          <ConnectButton />
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}

function NotConnected({ isGuard = false }: { isGuard?: boolean }) {
  const { isEnabled: isAiAgentEnabled } = useFlag("ai-linear-agent");
  const toast = useErrorToast();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("linear-error");

    if (error) {
      toast({
        title:
          error === "access_denied"
            ? "The connection to Linear was cancelled. You can try again anytime."
            : "The connection to Linear failed. Please try again.",
        status: error === "access_denied" ? "warning" : "error",
      });

      // clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [toast]);

  return (
    <VStack align="flex-start" spacing={2} w="full">
      <HStack justify="space-between" w="full">
        <Title />
        {isGuard && (
          <Badge colorScheme="orange">
            <HStack spacing={1}>
              <RiErrorWarningLine size={16} />
              <Text>Not connected</Text>
            </HStack>
          </Badge>
        )}
      </HStack>
      <HStack justify="space-between" w="full">
        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          <Text as="span">{description}</Text>{" "}
          {isAiAgentEnabled && (
            <Text as="span">
              Allow <strong>@reflag</strong> mentions to interact with your
              flags directly within Linear.
            </Text>
          )}
        </Text>

        <ButtonGroup alignSelf="flex-end">
          <DocumentationRefButton section="integrations/linear" />
          <ConnectButton />
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}

export default function LinearIntegrationForm({
  isGuard = false,
}: {
  isGuard?: boolean;
}) {
  const { data, isLoading } = useLinearConnection();

  return (
    <Card w="full">
      <CardBody w="full">
        {isLoading ? (
          <Center>
            <DelayedSpinner />
          </Center>
        ) : data && !data.needsReauthorization ? (
          <Connected linearConnection={data} />
        ) : data && data.needsReauthorization ? (
          <NeedsReauthorization />
        ) : (
          <NotConnected isGuard={isGuard} />
        )}
      </CardBody>
    </Card>
  );
}
